import { parseColorHexRGB } from "@microsoft/fast-colors";
import { blackOrWhiteByContrast } from "../color/index.js";
import { blackOrWhiteByContrastSet } from "../color/recipes/black-or-white-by-contrast-set.js";
import { contrastAndDeltaSwatchSet } from "../color/recipes/contrast-and-delta-swatch-set.js";
import { contrastSwatch } from "../color/recipes/contrast-swatch.js";
import { deltaSwatchSet } from "../color/recipes/delta-swatch-set.js";
import { deltaSwatch } from "../color/recipes/delta-swatch.js";
import { SwatchRGB } from "../color/swatch.js";
import { create, createNonCss } from "./create.js";
import { accentPalette, neutralPalette } from "./palette.js";
/**
 * Convenience values for WCAG contrast requirements.
 *
 * @public
 */
export const ContrastTarget = Object.freeze({
    /**
     * Minimum contrast for normal (<= 14pt) text (AA rating).
     */
    NormalText: 4.5,
    /**
     * Minimum contrast for large (> 14pt) text (AA rating).
     */
    LargeText: 3,
});
/** @public */
export const fillColor = create("fill-color").withDefault(SwatchRGB.from(parseColorHexRGB("#FFFFFF")));
// Accent Fill
/** @public */
export const accentFillMinContrast = createNonCss("accent-fill-min-contrast").withDefault(5.5);
/** @public */
export const accentFillRestDelta = createNonCss("accent-fill-rest-delta").withDefault(0);
/** @public */
export const accentFillHoverDelta = createNonCss("accent-fill-hover-delta").withDefault(-2);
/** @public */
export const accentFillActiveDelta = createNonCss("accent-fill-active-delta").withDefault(-5);
/** @public */
export const accentFillFocusDelta = createNonCss("accent-fill-focus-delta").withDefault(0);
/** @public */
export const accentFillRecipe = createNonCss("accent-fill-recipe").withDefault({
    evaluate: (element, reference) => contrastAndDeltaSwatchSet(accentPalette.getValueFor(element), reference || fillColor.getValueFor(element), accentFillMinContrast.getValueFor(element), accentFillRestDelta.getValueFor(element), accentFillHoverDelta.getValueFor(element), accentFillActiveDelta.getValueFor(element), accentFillFocusDelta.getValueFor(element)),
});
/** @public */
export const accentFillRest = create("accent-fill-rest").withDefault((element) => {
    return accentFillRecipe.getValueFor(element).evaluate(element).rest;
});
/** @public */
export const accentFillHover = create("accent-fill-hover").withDefault((element) => {
    return accentFillRecipe.getValueFor(element).evaluate(element).hover;
});
/** @public */
export const accentFillActive = create("accent-fill-active").withDefault((element) => {
    return accentFillRecipe.getValueFor(element).evaluate(element).active;
});
/** @public */
export const accentFillFocus = create("accent-fill-focus").withDefault((element) => {
    return accentFillRecipe.getValueFor(element).evaluate(element).focus;
});
// Foreground On Accent
/** @public */
export const foregroundOnAccentRecipe = createNonCss("foreground-on-accent-recipe").withDefault({
    evaluate: (element) => blackOrWhiteByContrastSet(accentFillRest.getValueFor(element), accentFillHover.getValueFor(element), accentFillActive.getValueFor(element), accentFillFocus.getValueFor(element), ContrastTarget.NormalText, false),
});
/** @public */
export const foregroundOnAccentRest = create("foreground-on-accent-rest").withDefault((element) => foregroundOnAccentRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const foregroundOnAccentHover = create("foreground-on-accent-hover").withDefault((element) => foregroundOnAccentRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const foregroundOnAccentActive = create("foreground-on-accent-active").withDefault((element) => foregroundOnAccentRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const foregroundOnAccentFocus = create("foreground-on-accent-focus").withDefault((element) => foregroundOnAccentRecipe.getValueFor(element).evaluate(element).focus);
// Accent Foreground
/** @public */
export const accentForegroundMinContrast = createNonCss("accent-foreground-min-contrast").withDefault(ContrastTarget.NormalText);
/** @public */
export const accentForegroundRestDelta = createNonCss("accent-foreground-rest-delta").withDefault(0);
/** @public */
export const accentForegroundHoverDelta = createNonCss("accent-foreground-hover-delta").withDefault(3);
/** @public */
export const accentForegroundActiveDelta = createNonCss("accent-foreground-active-delta").withDefault(-8);
/** @public */
export const accentForegroundFocusDelta = createNonCss("accent-foreground-focus-delta").withDefault(0);
/** @public */
export const accentForegroundRecipe = createNonCss("accent-foreground-recipe").withDefault({
    evaluate: (element, reference) => contrastAndDeltaSwatchSet(accentPalette.getValueFor(element), reference || fillColor.getValueFor(element), accentForegroundMinContrast.getValueFor(element), accentForegroundRestDelta.getValueFor(element), accentForegroundHoverDelta.getValueFor(element), accentForegroundActiveDelta.getValueFor(element), accentForegroundFocusDelta.getValueFor(element)),
});
/** @public */
export const accentForegroundRest = create("accent-foreground-rest").withDefault((element) => accentForegroundRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const accentForegroundHover = create("accent-foreground-hover").withDefault((element) => accentForegroundRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const accentForegroundActive = create("accent-foreground-active").withDefault((element) => accentForegroundRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const accentForegroundFocus = create("accent-foreground-focus").withDefault((element) => accentForegroundRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Foreground
/** @public */
export const neutralForegroundMinContrast = createNonCss("neutral-foreground-min-contrast").withDefault(16);
/** @public */
export const neutralForegroundRestDelta = createNonCss("neutral-foreground-rest-delta").withDefault(0);
/** @public */
export const neutralForegroundHoverDelta = createNonCss("neutral-foreground-hover-delta").withDefault(-19);
/** @public */
export const neutralForegroundActiveDelta = createNonCss("neutral-foreground-active-delta").withDefault(-30);
/** @public */
export const neutralForegroundFocusDelta = createNonCss("neutral-foreground-focus-delta").withDefault(0);
/** @public */
export const neutralForegroundRecipe = createNonCss("neutral-foreground-recipe").withDefault({
    evaluate: (element, reference) => contrastAndDeltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralForegroundMinContrast.getValueFor(element), neutralForegroundRestDelta.getValueFor(element), neutralForegroundHoverDelta.getValueFor(element), neutralForegroundActiveDelta.getValueFor(element), neutralForegroundFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralForegroundRest = create("neutral-foreground-rest").withDefault((element) => neutralForegroundRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralForegroundHover = create("neutral-foreground-hover").withDefault((element) => neutralForegroundRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralForegroundActive = create("neutral-foreground-active").withDefault((element) => neutralForegroundRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralForegroundFocus = create("neutral-foreground-focus").withDefault((element) => neutralForegroundRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Foreground Hint
/** @public */
export const neutralForegroundHintRecipe = createNonCss("neutral-foreground-hint-recipe").withDefault({
    evaluate: (element, reference) => contrastSwatch(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), ContrastTarget.NormalText),
});
/** @public */
export const neutralForegroundHint = create("neutral-foreground-hint").withDefault((element) => neutralForegroundHintRecipe.getValueFor(element).evaluate(element));
// Neutral Fill
/** @public */
export const neutralFillRestDelta = createNonCss("neutral-fill-rest-delta").withDefault(-1);
/** @public */
export const neutralFillHoverDelta = createNonCss("neutral-fill-hover-delta").withDefault(1);
/** @public */
export const neutralFillActiveDelta = createNonCss("neutral-fill-active-delta").withDefault(0);
/** @public */
export const neutralFillFocusDelta = createNonCss("neutral-fill-focus-delta").withDefault(0);
/** @public */
export const neutralFillRecipe = createNonCss("neutral-fill-recipe").withDefault({
    evaluate: (element, reference) => deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralFillRestDelta.getValueFor(element), neutralFillHoverDelta.getValueFor(element), neutralFillActiveDelta.getValueFor(element), neutralFillFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralFillRest = create("neutral-fill-rest").withDefault((element) => neutralFillRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralFillHover = create("neutral-fill-hover").withDefault((element) => neutralFillRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralFillActive = create("neutral-fill-active").withDefault((element) => neutralFillRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralFillFocus = create("neutral-fill-focus").withDefault((element) => neutralFillRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Fill Input
/** @public */
export const neutralFillInputRestDelta = createNonCss("neutral-fill-input-rest-delta").withDefault(-1);
/** @public */
export const neutralFillInputHoverDelta = createNonCss("neutral-fill-input-hover-delta").withDefault(1);
/** @public */
export const neutralFillInputActiveDelta = createNonCss("neutral-fill-input-active-delta").withDefault(0);
/** @public */
export const neutralFillInputFocusDelta = createNonCss("neutral-fill-input-focus-delta").withDefault(-2);
/** @public */
export const neutralFillInputRecipe = createNonCss("neutral-fill-input-recipe").withDefault({
    evaluate: (element, reference) => deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralFillInputRestDelta.getValueFor(element), neutralFillInputHoverDelta.getValueFor(element), neutralFillInputActiveDelta.getValueFor(element), neutralFillInputFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralFillInputRest = create("neutral-fill-input-rest").withDefault((element) => neutralFillInputRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralFillInputHover = create("neutral-fill-input-hover").withDefault((element) => neutralFillInputRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralFillInputActive = create("neutral-fill-input-active").withDefault((element) => neutralFillInputRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralFillInputFocus = create("neutral-fill-input-focus").withDefault((element) => neutralFillInputRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Fill Secondary
/** @public */
export const neutralFillSecondaryRestDelta = createNonCss("neutral-fill-secondary-rest-delta").withDefault(3);
/** @public */
export const neutralFillSecondaryHoverDelta = createNonCss("neutral-fill-secondary-hover-delta").withDefault(2);
/** @public */
export const neutralFillSecondaryActiveDelta = createNonCss("neutral-fill-secondary-active-delta").withDefault(1);
/** @public */
export const neutralFillSecondaryFocusDelta = createNonCss("neutral-fill-secondary-focus-delta").withDefault(3);
/** @public */
export const neutralFillSecondaryRecipe = createNonCss("neutral-fill-secondary-recipe").withDefault({
    evaluate: (element, reference) => deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralFillSecondaryRestDelta.getValueFor(element), neutralFillSecondaryHoverDelta.getValueFor(element), neutralFillSecondaryActiveDelta.getValueFor(element), neutralFillSecondaryFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralFillSecondaryRest = create("neutral-fill-secondary-rest").withDefault((element) => neutralFillSecondaryRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralFillSecondaryHover = create("neutral-fill-secondary-hover").withDefault((element) => neutralFillSecondaryRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralFillSecondaryActive = create("neutral-fill-secondary-active").withDefault((element) => neutralFillSecondaryRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralFillSecondaryFocus = create("neutral-fill-secondary-focus").withDefault((element) => neutralFillSecondaryRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Fill Stealth
/** @public */
export const neutralFillStealthRestDelta = createNonCss("neutral-fill-stealth-rest-delta").withDefault(0);
/** @public */
export const neutralFillStealthHoverDelta = createNonCss("neutral-fill-stealth-hover-delta").withDefault(3);
/** @public */
export const neutralFillStealthActiveDelta = createNonCss("neutral-fill-stealth-active-delta").withDefault(2);
/** @public */
export const neutralFillStealthFocusDelta = createNonCss("neutral-fill-stealth-focus-delta").withDefault(0);
/** @public */
export const neutralFillStealthRecipe = createNonCss("neutral-fill-stealth-recipe").withDefault({
    evaluate: (element, reference) => deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralFillStealthRestDelta.getValueFor(element), neutralFillStealthHoverDelta.getValueFor(element), neutralFillStealthActiveDelta.getValueFor(element), neutralFillStealthFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralFillStealthRest = create("neutral-fill-stealth-rest").withDefault((element) => neutralFillStealthRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralFillStealthHover = create("neutral-fill-stealth-hover").withDefault((element) => neutralFillStealthRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralFillStealthActive = create("neutral-fill-stealth-active").withDefault((element) => neutralFillStealthRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralFillStealthFocus = create("neutral-fill-stealth-focus").withDefault((element) => neutralFillStealthRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Fill Strong
/** @public */
export const neutralFillStrongMinContrast = createNonCss("neutral-fill-strong-min-contrast").withDefault(3);
/** @public */
export const neutralFillStrongRestDelta = createNonCss("neutral-fill-strong-rest-delta").withDefault(0);
/** @public */
export const neutralFillStrongHoverDelta = createNonCss("neutral-fill-strong-hover-delta").withDefault(8);
/** @public */
export const neutralFillStrongActiveDelta = createNonCss("neutral-fill-strong-active-delta").withDefault(-5);
/** @public */
export const neutralFillStrongFocusDelta = createNonCss("neutral-fill-strong-focus-delta").withDefault(0);
/** @public */
export const neutralFillStrongRecipe = createNonCss("neutral-fill-strong-recipe").withDefault({
    evaluate: (element, reference) => contrastAndDeltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralFillStrongMinContrast.getValueFor(element), neutralFillStrongRestDelta.getValueFor(element), neutralFillStrongHoverDelta.getValueFor(element), neutralFillStrongActiveDelta.getValueFor(element), neutralFillStrongFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralFillStrongRest = create("neutral-fill-strong-rest").withDefault((element) => neutralFillStrongRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralFillStrongHover = create("neutral-fill-strong-hover").withDefault((element) => neutralFillStrongRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralFillStrongActive = create("neutral-fill-strong-active").withDefault((element) => neutralFillStrongRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralFillStrongFocus = create("neutral-fill-strong-focus").withDefault((element) => neutralFillStrongRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Stroke
/** @public */
export const neutralStrokeRestDelta = createNonCss("neutral-stroke-rest-delta").withDefault(8);
/** @public */
export const neutralStrokeHoverDelta = createNonCss("neutral-stroke-hover-delta").withDefault(12);
/** @public */
export const neutralStrokeActiveDelta = createNonCss("neutral-stroke-active-delta").withDefault(6);
/** @public */
export const neutralStrokeFocusDelta = createNonCss("neutral-stroke-focus-delta").withDefault(8);
/** @public */
export const neutralStrokeRecipe = createNonCss("neutral-stroke-recipe").withDefault({
    evaluate: (element, reference) => {
        return deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralStrokeRestDelta.getValueFor(element), neutralStrokeHoverDelta.getValueFor(element), neutralStrokeActiveDelta.getValueFor(element), neutralStrokeFocusDelta.getValueFor(element));
    },
});
/** @public */
export const neutralStrokeRest = create("neutral-stroke-rest").withDefault((element) => neutralStrokeRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralStrokeHover = create("neutral-stroke-hover").withDefault((element) => neutralStrokeRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralStrokeActive = create("neutral-stroke-active").withDefault((element) => neutralStrokeRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralStrokeFocus = create("neutral-stroke-focus").withDefault((element) => neutralStrokeRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Stroke Divider
/** @public */
export const neutralStrokeDividerRestDelta = createNonCss("neutral-stroke-divider-rest-delta").withDefault(4);
/** @public */
export const neutralStrokeDividerRecipe = createNonCss("neutral-stroke-divider-recipe").withDefault({
    evaluate: (element, reference) => deltaSwatch(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralStrokeDividerRestDelta.getValueFor(element)),
});
/** @public */
export const neutralStrokeDividerRest = create("neutral-stroke-divider-rest").withDefault(element => neutralStrokeDividerRecipe.getValueFor(element).evaluate(element));
// Neutral Stroke Input
/** @public */
export const neutralStrokeInputRestDelta = createNonCss("neutral-stroke-input-rest-delta").withDefault(3);
/** @public */
export const neutralStrokeInputHoverDelta = createNonCss("neutral-stroke-input-hover-delta").withDefault(5);
/** @public */
export const neutralStrokeInputActiveDelta = createNonCss("neutral-stroke-input-active-delta").withDefault(5);
/** @public */
export const neutralStrokeInputFocusDelta = createNonCss("neutral-stroke-input-focus-delta").withDefault(5);
/** @public */
export const neutralStrokeInputRecipe = createNonCss("neutral-stroke-input-recipe").withDefault({
    evaluate: (element, reference) => {
        return deltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralStrokeInputRestDelta.getValueFor(element), neutralStrokeInputHoverDelta.getValueFor(element), neutralStrokeInputActiveDelta.getValueFor(element), neutralStrokeInputFocusDelta.getValueFor(element));
    },
});
/** @public */
export const neutralStrokeInputRest = create("neutral-stroke-input-rest").withDefault((element) => neutralStrokeInputRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralStrokeInputHover = create("neutral-stroke-input-hover").withDefault((element) => neutralStrokeInputRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralStrokeInputActive = create("neutral-stroke-input-active").withDefault((element) => neutralStrokeInputRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralStrokeInputFocus = create("neutral-stroke-input-focus").withDefault((element) => neutralStrokeInputRecipe.getValueFor(element).evaluate(element).focus);
// Neutral Stroke Strong
/** @public */
export const neutralStrokeStrongMinContrast = createNonCss("neutral-stroke-strong-min-contrast").withDefault(5.5);
/** @public */
export const neutralStrokeStrongRestDelta = createNonCss("neutral-stroke-strong-rest-delta").withDefault(0);
/** @public */
export const neutralStrokeStrongHoverDelta = createNonCss("neutral-stroke-strong-hover-delta").withDefault(0);
/** @public */
export const neutralStrokeStrongActiveDelta = createNonCss("neutral-stroke-strong-active-delta").withDefault(0);
/** @public */
export const neutralStrokeStrongFocusDelta = createNonCss("neutral-stroke-strong-focus-delta").withDefault(0);
/** @public */
export const neutralStrokeStrongRecipe = createNonCss("neutral-stroke-strong-recipe").withDefault({
    evaluate: (element, reference) => contrastAndDeltaSwatchSet(neutralPalette.getValueFor(element), reference || fillColor.getValueFor(element), neutralStrokeStrongMinContrast.getValueFor(element), neutralStrokeStrongRestDelta.getValueFor(element), neutralStrokeStrongHoverDelta.getValueFor(element), neutralStrokeStrongActiveDelta.getValueFor(element), neutralStrokeStrongFocusDelta.getValueFor(element)),
});
/** @public */
export const neutralStrokeStrongRest = create("neutral-stroke-strong-rest").withDefault((element) => neutralStrokeStrongRecipe.getValueFor(element).evaluate(element).rest);
/** @public */
export const neutralStrokeStrongHover = create("neutral-stroke-strong-hover").withDefault((element) => neutralStrokeStrongRecipe.getValueFor(element).evaluate(element).hover);
/** @public */
export const neutralStrokeStrongActive = create("neutral-stroke-strong-active").withDefault((element) => neutralStrokeStrongRecipe.getValueFor(element).evaluate(element).active);
/** @public */
export const neutralStrokeStrongFocus = create("neutral-stroke-strong-focus").withDefault((element) => neutralStrokeStrongRecipe.getValueFor(element).evaluate(element).focus);
// Focus Stroke Outer
/** @public */
export const focusStrokeOuterRecipe = createNonCss("focus-stroke-outer-recipe").withDefault({
    evaluate: (element) => blackOrWhiteByContrast(fillColor.getValueFor(element), ContrastTarget.NormalText, true),
});
/** @public */
export const focusStrokeOuter = create("focus-stroke-outer").withDefault((element) => focusStrokeOuterRecipe.getValueFor(element).evaluate(element));
// Focus Stroke Inner
/** @public */
export const focusStrokeInnerRecipe = createNonCss("focus-stroke-inner-recipe").withDefault({
    evaluate: (element) => blackOrWhiteByContrast(focusStrokeOuter.getValueFor(element), ContrastTarget.NormalText, false),
});
/** @public */
export const focusStrokeInner = create("focus-stroke-inner").withDefault((element) => focusStrokeInnerRecipe.getValueFor(element).evaluate(element));
